<template>
    <div>
        <div
            data-nav
            :data-has-scrolled="hasScrolled"
            class="relative z-10 flex h-[92px] items-start px-11 py-6 transition-all duration-300 lg:h-[128px]"
            :class="{
                'surface-primary border-neutral-subtle border-b bg-none lg:h-[92px]!': hasScrolled || showBackground,
            }"
        >
            <!-- logo -->
            <div
                id="nav-logo"
                class="flex h-full flex-1 cursor-pointer items-center [&_img]:max-h-full"
                :class="{
                    'order-1': logoPosition === 'left',
                    'order-3 justify-center': logoPosition === 'centred',
                }"
            >
                <NuxtLink
                    :class="{
                        'h-full': logoPosition === 'left',
                        'absolute inset-y-6 left-1/2 -translate-x-1/2': logoPosition === 'centred',
                    }"
                    :to="homeUri"
                    no-prefetch
                    aria-label="Home"
                >
                    <slot name="logo" />
                </NuxtLink>
            </div>

            <!-- Google Translate -->
            <GoogleTranslate
                v-if="googleTranslateSettings?.toggle"
                :languages="googleTranslateSettings?.options"
                :order="3"
            />

            <!-- search -->
            <div
                v-if="showSearch"
                class="hidden items-center space-x-4 md:flex"
                :class="{
                    'order-2 mr-2 ml-auto': logoPosition === 'left',
                    'order-4': logoPosition === 'centred',
                }"
            >
                <NuxtLink
                    to="/search"
                    no-prefetch
                    aria-label="Search"
                >
                    <DButton
                        theme="transparent"
                        icon="ph:magnifying-glass"
                        size="lg"
                    />
                </NuxtLink>
                <!-- line -->
                <span
                    v-if="showLine"
                    class="block h-[22px] w-px bg-(--border-neutral-base)"
                />
            </div>

            <!-- quicklinks -->
            <div
                v-if="quicklinks?.mainNavQuicklinksItem_Links && quicklinks.mainNavQuicklinksItem_Links.length > 0"
                class="mx-2 hidden sm:inline-block"
                :class="{
                    'order-3': logoPosition === 'left',
                    'order-2': logoPosition === 'centred',
                }"
            >
                <DDropdown>
                    <template #trigger="dropdownProps">
                        <DButton
                            theme="transparent"
                            right-icon="ph:caret-down"
                            size="lg"
                            :active="dropdownProps.active"
                        >
                            {{ quicklinks.mainNavQuicklinksItem_Heading }}
                        </DButton>
                    </template>

                    <NuxtLink
                        v-for="(quicklink, index) in quicklinks.mainNavQuicklinksItem_Links"
                        :key="index"
                        :to="quicklink.url"
                        :target="quicklink.target"
                        no-prefetch
                    >
                        <DDropdownItem
                            tag="div"
                            :leading-icon="iconName(quicklink?.icon)"
                        >
                            {{ quicklink.text }}
                        </DDropdownItem>
                    </NuxtLink>
                </DDropdown>
            </div>

            <!-- buttons -->
            <div
                v-if="buttons.length > 0"
                class="mx-2 hidden items-center space-x-2 lg:flex"
                :class="{
                    'order-4': logoPosition === 'left',
                    'order-5': logoPosition === 'centred',
                }"
            >
                <NuxtLink
                    v-for="(button, buttonIndex) in buttons"
                    :key="buttonIndex"
                    :to="button.url"
                    :target="button.target"
                    no-prefetch
                >
                    <DButton
                        :theme="buttonTheme(button.buttonStyle)"
                        size="lg"
                    >
                        {{ button.text }}
                    </DButton>
                </NuxtLink>
            </div>

            <!-- menu -->
            <div
                class="shrink-0"
                :class="{
                    'order-5': logoPosition === 'left',
                    'order-1': logoPosition === 'centred',
                }"
            >
                <div class="hidden lg:flex">
                    <DButton
                        id="menubutton"
                        class="menu-button"
                        right-icon="ph:list"
                        :theme="settings?.primaryNav_MenuButtonStyle === 'outline' ? 'outline' : 'brand'"
                        size="lg"
                        type="button"
                        aria-haspopup="true"
                        aria-controls="hamburger-menu"
                        aria-expanded="false"
                        @click="showHamburgerMenu = true"
                    >
                        Menu
                    </DButton>
                </div>

                <div class="lg:hidden">
                    <DButton
                        class="menu-button"
                        icon="ph:list"
                        :theme="settings?.primaryNav_MenuButtonStyle === 'outline' ? 'outline' : 'brand'"
                        size="lg"
                        type="button"
                        aria-haspopup="true"
                        aria-controls="hamburger-menu"
                        aria-expanded="false"
                        @click="showHamburgerMenu = true"
                    />
                </div>
            </div>
        </div>

        <MenuHamburger
            v-if="showHamburgerMenu"
            :settings="settings"
            :entries="entries"
            :position="logoPosition === 'centred' ? 'left' : 'right'"
            :show-search="showSearch"
            @close="showHamburgerMenu = false"
        />
    </div>
</template>

<script setup>
    import { DDropdown, DDropdownItem, DButton } from '@digistorm/spark'

    import { useGlobalStore } from '../../../stores/global'

    const { iconName } = useIcons()
    const { pageType } = storeToRefs(useGlobalStore())

    const props = defineProps({
        settings: Object,
        entries: Array,
        homeUri: String,
        showSearch: Boolean,
        googleTranslateSettings: Object,
    })

    const { buttonTheme } = useButtonTheme()
    const { showHamburgerMenu, hasScrolled } = storeToRefs(useGlobalStore())

    const showBackground = computed(() => {
        return pageType.value === 'home'
            ? props.settings?.primaryNav_ShowBackground
            : props.settings?.primaryNav_InternalPageShowBackground
    })

    const logoPosition = computed(() => {
        return props.settings.primaryNav_LogoPosition
    })

    const quicklinks = computed(() => {
        return first(props.settings.primaryNav_Quicklinks)
    })

    const buttons = computed(() => {
        return props.settings.primaryNav_Buttons
            ? props.settings.primaryNav_Buttons.filter((button) => !button.isEmpty)
            : []
    })

    const showLine = computed(() => {
        // Shows the vertical pipe if there are Quicklinks or Google translate is enabled
        const hasQuicklinks = props.settings.primaryNav_Quicklinks?.length
        const isGoogleTranslateEnabled = props.googleTranslateSettings?.toggle || false
        if (logoPosition.value === 'left') {
            return hasQuicklinks || isGoogleTranslateEnabled
        }
        return false
    })
</script>
